import { Form } from "react-bootstrap";
import TextField from "@components/FormsV2/TextField/TextField";
import GenericToggle from "@components/FormsV2/GenericToggle/GenericToggle";
import RequiredReactSelect from "@components/FormsV2/RequiredReactSelect/RequiredReactSelect";
import { SelectOptionsEditor } from "./SelectOptionsEditor";
import {
  VALUE_TYPE_OPTIONS,
  STEP_OPTIONS,
  DATE_MIN_OPTIONS,
  DATE_MAX_OPTIONS,
  hasMinMax,
  hasStep,
  hasSelectOptions,
  type QuestionFormValues,
  type ValueType,
  type QuestionSettings,
  type SelectOption,
} from "./types";
import "./style.css";

export interface QuestionFormProps {
  values: QuestionFormValues;
  onChange: (next: QuestionFormValues) => void;
  mode: "create" | "edit";
  slug: string;
}

export const QuestionForm = ({
  values,
  onChange,
  mode,
  slug,
}: QuestionFormProps): JSX.Element => {
  const set = <K extends keyof QuestionFormValues>(
    key: K,
    v: QuestionFormValues[K]
  ) => onChange({ ...values, [key]: v });

  const setSetting = <K extends keyof QuestionSettings>(
    key: K,
    v: QuestionSettings[K]
  ) => onChange({ ...values, settings: { ...values.settings, [key]: v } });

  const vt = values.valueType;
  const minMaxLabel =
    vt === "number" ? "Value" : vt === "date" ? "Date" : "Length";

  return (
    <Form className="question-form">
      <Form.Group className="mb-3">
        <Form.Label htmlFor="q-question">Question</Form.Label>
        <TextField
          label={null}
          id="q-question"
          value={values.question}
          onChange={(e) => set("question", e.target.value)}
          required
        />
      </Form.Group>

      <div className="row">
        <div className="col-sm-6">
          <Form.Group className="mb-3">
            <Form.Label htmlFor="q-name">Name</Form.Label>
            <TextField
              label={null}
              id="q-name"
              value={values.name}
              onChange={(e) => set("name", e.target.value)}
              required
            />
            <small className="text-muted d-block">
              Used when showing answers.
            </small>
          </Form.Group>
        </div>
        {mode === "edit" && (
          <div className="col-sm-6">
            <Form.Group className="mb-3">
              <Form.Label htmlFor="q-slug">Slug</Form.Label>
              <TextField
                label={null}
                id="q-slug"
                value={slug}
                onChange={() => undefined}
                readOnly
                disabled
              />
              <small className="text-muted d-block">
                Used in calculations and lookups.
              </small>
            </Form.Group>
          </div>
        )}
      </div>

      <div className="row">
        <div className="col-sm-6">
          <RequiredReactSelect
            label="Type of Value"
            value={VALUE_TYPE_OPTIONS.find((o) => o.value === vt)}
            onChange={(opt: any) =>
              set("valueType", (opt?.value ?? vt) as ValueType)
            }
            options={VALUE_TYPE_OPTIONS}
            isDisabled={mode === "edit"}
          />
        </div>
        <div className="col-sm-6">
          <Form.Label>Required?</Form.Label>
          <div>
            <GenericToggle
              key={`required-${mode}`}
              initialValueOfIsOn={values.required}
              onToggle={(on: boolean) => set("required", on)}
              onText="Required"
              offText="Optional"
              hasText
            />
          </div>
        </div>
      </div>

      {hasMinMax(vt) && vt !== "date" && (
        <div className="row">
          <div className="col-sm-4">
            <Form.Group className="mb-3">
              <Form.Label htmlFor="q-min">{`Minimum ${minMaxLabel}`}</Form.Label>
              <Form.Control
                id="q-min"
                type="number"
                value={values.settings.min}
                onChange={(e) => setSetting("min", e.target.value)}
              />
            </Form.Group>
          </div>
          <div className="col-sm-4">
            <Form.Group className="mb-3">
              <Form.Label htmlFor="q-max">{`Maximum ${minMaxLabel}`}</Form.Label>
              <Form.Control
                id="q-max"
                type="number"
                value={values.settings.max}
                onChange={(e) => setSetting("max", e.target.value)}
              />
            </Form.Group>
          </div>
          {hasStep(vt) && (
            <div className="col-sm-4">
              <RequiredReactSelect
                label="Decimal Places"
                value={STEP_OPTIONS.find(
                  (o) => o.value === values.settings.step
                )}
                onChange={(opt: any) =>
                  setSetting("step", opt ? Number(opt.value) : "")
                }
                options={STEP_OPTIONS}
              />
            </div>
          )}
        </div>
      )}

      {vt === "date" && (
        <div className="row">
          <div className="col-sm-6">
            <RequiredReactSelect
              label="Minimum Date"
              value={DATE_MIN_OPTIONS.find(
                (o) => o.value === values.settings.min
              )}
              onChange={(opt: any) => setSetting("min", opt?.value ?? "")}
              options={DATE_MIN_OPTIONS}
            />
          </div>
          <div className="col-sm-6">
            <RequiredReactSelect
              label="Maximum Date"
              value={DATE_MAX_OPTIONS.find(
                (o) => o.value === values.settings.max
              )}
              onChange={(opt: any) => setSetting("max", opt?.value ?? "")}
              options={DATE_MAX_OPTIONS}
            />
          </div>
        </div>
      )}

      {hasSelectOptions(vt) && (
        <SelectOptionsEditor
          value={values.selectOptions}
          onChange={(next: SelectOption[]) => set("selectOptions", next)}
        />
      )}
    </Form>
  );
};

export default QuestionForm;
